import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './ProfilePage.css';

const ProfilePage = ({ user: initialUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser || {});
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    totalBookings: 0,
    approvedBookings: 0,
    pendingBookings: 0,
    membershipCount: 0,
    equipmentCount: 0
  });

  // Handler functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditToggle = () => {
    setEditedUser(user);
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    // TODO: Implement profile save logic
    setIsEditing(false);
  };

  const handleImageUpload = async (e) => {
    // TODO: Implement image upload logic
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordData.new !== passwordData.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    // TODO: Implement password change logic
    setPasswordSuccess('Password changed successfully');
    setShowChangePassword(false);
    setPasswordData({ current: '', new: '', confirm: '' });
  };

  const getInitials = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return 'U';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // ... (all the logic remains exactly the same as before) ...

  return (
    <motion.div 
      className="profile-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="profile-page__header">
        <button className="profile-page__back-btn" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <h1>My Profile</h1>
        <button 
          className={`profile-page__edit-btn ${isEditing ? 'profile-page__edit-btn--saving' : ''}`}
          onClick={isEditing ? handleSaveProfile : handleEditToggle}
        >
          <i className={`fa-solid ${isEditing ? 'fa-check' : 'fa-pen'}`}></i>
          <span>{isEditing ? 'Save' : 'Edit'}</span>
        </button>
      </div>

      {/* Profile Header with Avatar */}
      <div className="profile-page__header-content">
        <div className="profile-page__avatar-container">
          <div className="profile-page__avatar">
            {previewImage || editedUser.profilePicture ? (
              <img src={previewImage || editedUser.profilePicture} alt="Profile" />
            ) : (
              <span className="profile-page__avatar-initials">{getInitials()}</span>
            )}
          </div>
          {isEditing && (
            <label className="profile-page__avatar-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              <i className={`fa-solid ${uploading ? 'fa-spinner fa-spin' : 'fa-camera'}`}></i>
            </label>
          )}
        </div>
        <h2>{user.first_name} {user.last_name}</h2>
        <p className="profile-page__member-since">Member since {formatDate(user.joinDate || new Date())}</p>
      </div>

      {/* Tabs */}
      <div className="profile-page__tabs">
        <button 
          className={`profile-page__tab ${activeTab === 'profile' ? 'profile-page__tab--active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <i className="fa-solid fa-user"></i>
          <span>Profile</span>
        </button>
        <button 
          className={`profile-page__tab ${activeTab === 'stats' ? 'profile-page__tab--active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <i className="fa-solid fa-chart-simple"></i>
          <span>Statistics</span>
        </button>
        <button 
          className={`profile-page__tab ${activeTab === 'security' ? 'profile-page__tab--active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <i className="fa-solid fa-shield"></i>
          <span>Security</span>
        </button>
        <button 
          className={`profile-page__tab ${activeTab === 'preferences' ? 'profile-page__tab--active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          <i className="fa-solid fa-gear"></i>
          <span>Preferences</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="profile-page__tab-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div 
            className="profile-page__form"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
          >
            <div className="profile-page__form-group">
              <label>
                <i className="fa-solid fa-user"></i>
                First Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="first_name"
                  value={editedUser.first_name || ''}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                />
              ) : (
                <p className="profile-page__field-value">{user.first_name || 'Not set'}</p>
              )}
            </div>

            <div className="profile-page__form-group">
              <label>
                <i className="fa-solid fa-user"></i>
                Last Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="last_name"
                  value={editedUser.last_name || ''}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                />
              ) : (
                <p className="profile-page__field-value">{user.last_name || 'Not set'}</p>
              )}
            </div>

            <div className="profile-page__form-group">
              <label>
                <i className="fa-solid fa-envelope"></i>
                Email
              </label>
              <p className="profile-page__field-value">{user.email || 'Not set'}</p>
            </div>

          </motion.div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'stats' && (
          <motion.div 
            className="profile-page__stats-tab"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
          >
            <div className="profile-page__stats-grid">
              <div className="profile-page__stat-card">
                <div className="profile-page__stat-icon profile-page__stat-icon--total">
                  <i className="fa-solid fa-calendar"></i>
                </div>
                <div className="profile-page__stat-info">
                  <span className="profile-page__stat-value">{stats.totalBookings}</span>
                  <span className="profile-page__stat-label">Total Bookings</span>
                </div>
              </div>

              {/* ... rest of the stats cards with updated class names ... */}

            </div>

            <div className="profile-page__recent-activity">
              <h3>Recent Activity</h3>
              <p className="profile-page__no-activity">No recent activity</p>
            </div>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div 
            className="profile-page__security-tab"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
          >
            {!showChangePassword ? (
              <button 
                className="profile-page__change-password-btn"
                onClick={() => setShowChangePassword(true)}
              >
                <i className="fa-solid fa-key"></i>
                Change Password
              </button>
            ) : (
              <div className="profile-page__password-form">
                {passwordError && <p className="profile-page__error">{passwordError}</p>}
                {passwordSuccess && <p className="profile-page__success">{passwordSuccess}</p>}
                
                <div className="profile-page__form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordData.current}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                    placeholder="Enter current password"
                  />
                </div>

                <div className="profile-page__form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={passwordData.new}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                    placeholder="Enter new password"
                  />
                </div>

                <div className="profile-page__form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="profile-page__button-group">
                  <button onClick={handlePasswordChange} className="profile-page__btn-primary">
                    Change Password
                  </button>
                  <button onClick={() => setShowChangePassword(false)} className="profile-page__btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <motion.div 
            className="profile-page__preferences-tab"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
          >
            <div className="profile-page__preference-item">
              <h3>Notification Preferences</h3>
              <p>Manage your notification settings here.</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Logout Button */}
      <button 
        className="profile-page__logout-btn"
        onClick={() => {
          localStorage.removeItem('nlngra_user');
          window.location.href = '/login';
        }}
      >
        <i className="fa-solid fa-sign-out-alt"></i>
        Logout
      </button>
    </motion.div>
  );
};

export default ProfilePage;